<template>
    <div class="category-lookup" :class="{ 'disabled': isDisabledState }" v-if="!errorMessage">
        <div class="mobile-category-button"
             :class="{ 'disabled': isDisabledState }"
             role="button"
             :tabindex="isDisabledState ? -1 : 0"
             :aria-expanded="isMobileCategoryMenuVisible ? 'true' : 'false'"
             aria-controls="category-filter-menu"
             @click="!isDisabledState ? toggleCategoryMenu() : null"
             @keydown.enter.prevent="!isDisabledState ? toggleCategoryMenu() : null"
             @keydown.space.prevent="!isDisabledState ? toggleCategoryMenu() : null">{{ selectedCategoryName }}</div>
        <div id="category-filter-menu" class="category-filter-menu" :class="{ 'mobile-visible': isMobileCategoryMenuVisible }">
            <button v-for="cat in categories" :key="cat.id" :id="cat.id" 
                    :class="{active: selectedCategory === cat.id, disabled: isDisabledState}" 
                    @click="!isDisabledState ? selectCategory(cat) : null">{{ cat.label }}</button>
        </div>
        <div class="category-cards" :class="{ 'disabled': isDisabledState }">
            <div class="scroll-arrow left-arrow" :class="{ 'disabled': isDisabledState }"
                 role="button"
                 :tabindex="(!isDisabledState && canScrollLeft) ? 0 : -1"
                 :aria-disabled="(isDisabledState || !canScrollLeft) ? 'true' : 'false'"
                 @click="!isDisabledState ? scrollLeft() : null"
                 @keydown.enter.prevent="!isDisabledState ? scrollLeft() : null"
                 @keydown.space.prevent="!isDisabledState ? scrollLeft() : null"
                 v-show="!loading">
                <span v-if="canScrollLeft">&lsaquo;</span>
            </div>
            <category-cards-skeleton v-if="loading" :card-count="6"></category-cards-skeleton>

            <template v-else>
                <div v-if="filterCards.length === 0 && selectedCategory" class="no-cards-message">No cards found for this category.</div>
                <div v-else class="card-list-container">
                    <div class="card-list" ref="cardList">
                        <div
                            v-for="card in filterCards"
                            :key="card.cardId"
                            class="card-item"
                            role="button"
                            :tabindex="(!isDisabledState && !card.isAlreadySelected) ? 0 : -1"
                            :aria-disabled="(isDisabledState || card.isAlreadySelected) ? 'true' : 'false'"
                            :aria-label="getCardAriaLabel(card)"
                            @click="!isDisabledState ? addToCards(card.cardId) : null"
                            @keydown.enter.prevent="!isDisabledState ? addToCards(card.cardId) : null"
                            @keydown.space.prevent="!isDisabledState ? addToCards(card.cardId) : null"
                        >
                            <div class="card-img" :class="{ 'already-selected': card.isAlreadySelected }">
                                <img :src="card.imageUrl" :alt="card.name" v-if="card.imageUrl" />
                                <div class="overlay" v-if="!card.isAlreadySelected">
                                    <span class="plus-icon">+</span>
                                </div>
                            </div>
                            <div class="card-title" v-html="card.name"></div>
                        </div>
                    </div>
                </div>
            </template>

            <div class="scroll-arrow right-arrow" :class="{ 'disabled': isDisabledState }"
                 role="button"
                 :tabindex="(!isDisabledState && canScrollRight) ? 0 : -1"
                 :aria-disabled="(isDisabledState || !canScrollRight) ? 'true' : 'false'"
                 @click="!isDisabledState ? scrollRight() : null"
                 @keydown.enter.prevent="!isDisabledState ? scrollRight() : null"
                 @keydown.space.prevent="!isDisabledState ? scrollRight() : null"
                 v-show="!loading">
                <span v-if="canScrollRight">&rsaquo;</span>
            </div>
        </div>
    </div>
</template>

<script>
import CardService from "../../shared/services/card-service";
import SavedCompareCardsMixin from "../../shared/mixins/saved-compare-cards";
import CategoryCardsSkeleton from '../shared/skeletons/CategoryCardsSkeleton.vue';

export default {
    name: "category-lookup",
    mixins: [SavedCompareCardsMixin],
    components: {
        CategoryCardsSkeleton
    },
    props: {
        cards: {
            type: Array,
            default: () => [],
            required: true
        },
        externalLimitReached: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            categories: [
                { id: "best-credit-card-offers", label: "Top Credit Cards" },
                { id: "balance-transfer", label: "Balance Transfer" },
                { id: "low-interest", label: "Low Interest" },
                { id: "reward", label: "Rewards" },
                { id: "travel", label: "Travel" },
                { id: "cash-back", label: "Cash Back" },
                { id: "airline", label: "Airline" },
                { id: "hotel", label: "Hotel" },
            ],
            selectedCategory: null,
            filterCards: [],
            loading: false,
            errorMessage: false,
            maxCompareLimit: 4,
            canScrollLeft: false,
            canScrollRight: false,
            scrollTimeout: null,
            isMobileCategoryMenuVisible: false,
            selectedCategoryName: 'Category',
            hasAttemptedAction: false,
        };
    },
    computed: {
        isLimitReached() {
            // Show error message only when user has attempted action and limit is reached
            return this.hasAttemptedAction && this.cards.length >= 4;
        },
        isDisabledState() {
            // Simplified computed property for disable logic
            return this.isLimitReached;
        }
    },
    watch: {
        cards: function(newCards, oldCards) {
            // Reset the attempted action flag when cards are removed
            if (newCards.length < oldCards.length) {
                this.hasAttemptedAction = false;
            }
            // Update the isAlreadySelected state when cards change
            this.updateSelectedState();
        },
        externalLimitReached: function(newVal) {
            // When external component (like SearchBar) hits limit, mark attempted action
            // Only disable when someone tried to add a 5th card (not when we have exactly 4)
            if (newVal) {
                this.hasAttemptedAction = true;
            }
        }
    },
    mounted() {
        this.selectCategory({id: "best-credit-card-offers"});
        this.attachScrollListener();
    },
    beforeDestroy() {
        // Clean up event listener and timeout
        const cardList = this.$refs.cardList;
        if (cardList) {
            cardList.removeEventListener('scroll', this.handleScroll);
        }
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
    },
    methods: {
        getCardAriaLabel(card) {
            if (card.isAlreadySelected) {
                return `${card.name} — already selected`;
            }
            return `Add ${card.name} to comparison`;
        },
        updateSelectedState() {
            // Update the isAlreadySelected property for all cards in filterCards
            if (this.filterCards.length > 0) {
                const selectedCardIds = this.cards.map(card => card.cardId);
                this.filterCards = this.filterCards.map(card => ({
                    ...card,
                    isAlreadySelected: selectedCardIds.includes(card.cardId)
                }));
            }
        },
        scrollLeft() {
            // Don't scroll if disabled or arrow shouldn't be visible
            if (this.hasAttemptedAction && this.cards.length >= 4) {
                return;
            }
            if (!this.canScrollLeft) {
                return;
            }

            const cardList = this.$refs.cardList;
            if (cardList) {
                cardList.scrollBy({ left: -300, behavior: 'smooth' });
                // Update buttons after scroll animation completes
                setTimeout(() => {
                    this.updateScrollButtons();
                }, 100);
            }
        },
        scrollRight() {
            // Don't scroll if disabled or arrow shouldn't be visible
            if (this.hasAttemptedAction && this.cards.length >= 4) {
                return;
            }
            if (!this.canScrollRight) {
                return;
            }

            const cardList = this.$refs.cardList;
            if (cardList) {
                cardList.scrollBy({ left: 300, behavior: 'smooth' });
                // Update buttons after scroll animation completes
                setTimeout(() => {
                    this.updateScrollButtons();
                }, 100);
            }
        },
        updateScrollButtons() {
            this.$nextTick(() => {
                const cardList = this.$refs.cardList;
                if (cardList) {
                    // Add a small tolerance (1px) to account for sub-pixel scrolling
                    const scrollLeft = cardList.scrollLeft;
                    const maxScrollLeft = cardList.scrollWidth - cardList.clientWidth;

                    this.canScrollLeft = scrollLeft > 1;
                    this.canScrollRight = scrollLeft < (maxScrollLeft - 1);
                } else {
                    this.canScrollLeft = false;
                    this.canScrollRight = false;
                }
            });
        },
        handleScroll() {
            // Debounce scroll events for better performance
            if (this.scrollTimeout) {
                clearTimeout(this.scrollTimeout);
            }
            this.scrollTimeout = setTimeout(() => {
                this.updateScrollButtons();
            }, 10);
        },
        attachScrollListener() {
            this.$nextTick(() => {
                const cardList = this.$refs.cardList;
                if (cardList) {
                    // Remove existing listener first to avoid duplicates
                    cardList.removeEventListener('scroll', this.handleScroll);
                    // Add the scroll event listener
                    cardList.addEventListener('scroll', this.handleScroll);
                }
            });
        },
        async selectCategory(cat) {
            // Don't allow category selection if limit is reached
            if (this.isDisabledState) {
                return;
            }
            
            this.selectedCategory = cat.id;
            this.filterCards = [];
            if (this.isInitialized) {
                this.selectedCategoryName = cat.label;
            }
            this.isInitialized = true;
            this.loading = true;
            // Close mobile menu when category is selected
            this.isMobileCategoryMenuVisible = false;
            // Hide arrows while loading new category
            this.canScrollLeft = false;
            this.canScrollRight = false;
            try {
                const res = await CardService.getTopCards(this.cards, this.selectedCategory);
                if (res && res.data) {
                    // Get the currently selected card IDs from the parent component
                    const selectedCardIds = this.cards.map(card => card.cardId);

                    // Mark cards as already selected if they exist in the selected cards array
                    this.filterCards = res.data.map(card => ({
                        ...card,
                        isAlreadySelected: selectedCardIds.includes(card.cardId)
                    }));
                } else {
                    this.filterCards = [];
                }
            } catch (e) {
                this.filterCards = [];
            } finally {
                this.loading = false;
                this.updateScrollButtons();
                this.attachScrollListener();
            }
        },
        toggleCategoryMenu() {
            // Don't toggle if disabled
            if (this.hasAttemptedAction && this.cards.length >= 4) {
                return;
            }
            this.isMobileCategoryMenuVisible = !this.isMobileCategoryMenuVisible;
        },
        addToCards: function(cardId) {
                // Check if card is already selected
                const selectedCardIds = this.cards.map(card => card.cardId);
                if (selectedCardIds.includes(cardId)) {
                    console.log(`Card ${cardId} is already selected`);
                    return; // Don't add if already selected
                }

                // Check if limit is reached BEFORE attempting action
                if (this.cards.length >= 4) {
                    // Mark that user has attempted an action
                    this.hasAttemptedAction = true;
                    this.$emit('limit-reached');
                    return;
                }
                this.hasAttemptedAction = false;

            var commonMarketingParams = analyticsTracking.getLTCookieParams();
            var trackData = analyticsTracking.mapTrackingParams(commonMarketingParams);
            trackData.creditCard_data = {
                card_id: cardId,
                card_page: window.location.href
            };
            analyticsTracking.track("Comparison Filter Card Engaged", trackData);

            CardService.getCard(siteId, cardId).then((res) => {
                console.log(res);
                if (res && res.data) {
                    // Use mixin method for consistent card adding logic
                    this.addCardToCompare(res.data);

                    // Update the filterCards to reflect the new selection
                    this.filterCards = this.filterCards.map(card => ({
                        ...card,
                        isAlreadySelected: card.cardId === cardId ? true : card.isAlreadySelected
                    }));
                }
                else {
                    console.log(`Could not find card ${cardId}`);
                }
            });
        }
    }
};
</script>
<style src="../../shared/sass/lendingtree/category-lookup.scss" lang="scss"></style>